import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, Button, Badge, Input, Select, Modal, Table, StatCard } from '../components/Common';

const SCAN_API = '/orchestrator-api/api';

const STATUS_COLORS = {
  queued: { bg: '#1e3a5f', color: '#93c5fd' },
  running: { bg: '#065f46', color: '#6ee7b7' },
  completed: { bg: '#14532d', color: '#86efac' },
  failed: { bg: '#991b1b', color: '#fca5a5' },
  cancelled: { bg: '#44403c', color: '#d6d3d1' },
};

const TOOL_ICONS = {
  nuclei: '☢️', nmap: '🔍', header_check: '📋', ssl_analyzer: '🔒', dir_brute: '📂',
};

// Kept in step with VALID_TYPES in backend/services/engagement-types.js, which
// is the authoritative list. This is one of several copies -- Engagements.js,
// EngagementDetail.js and PlaybookTemplates.js each hold their own, and
// Governance.js holds a deliberately application-only subset. Adding a type
// means touching all of them; the last one added was missed in
// EngagementDetail.js, which blanked the field when editing such an
// engagement.
const ENG_TYPES = [
  'Application Penetration Test',
  'Network Penetration Test',
  'Network Segmentation',
  'API Penetration Test',
  'Mobile Application Test',
  'Red Team',
];

// Available scan tools with their configurable options
const AVAILABLE_TOOLS = [
  {
    id: 'nuclei',
    name: 'Nuclei',
    icon: '☢️',
    description: 'Template-based vulnerability scanner. Detects CVEs, misconfigurations, exposed panels, and more.',
    category: 'Vulnerability Scanning',
    defaults: {
      templates: ['http', 'cves', 'vulnerabilities', 'misconfiguration', 'exposed-panels'],
      severities: ['critical', 'high', 'medium', 'low'],
      concurrency: 25,
      rate_limit: 150,
    },
    options: {
      templates: {
        label: 'Template Categories',
        type: 'multi-select',
        choices: [
          { value: 'http', label: 'HTTP' },
          { value: 'cves', label: 'CVEs' },
          { value: 'vulnerabilities', label: 'Vulnerabilities' },
          { value: 'misconfiguration', label: 'Misconfiguration' },
          { value: 'exposed-panels', label: 'Exposed Panels' },
          { value: 'default-logins', label: 'Default Logins' },
          { value: 'network', label: 'Network' },
          { value: 'dns', label: 'DNS' },
          { value: 'file', label: 'File' },
          { value: 'headless', label: 'Headless' },
        ],
      },
      severities: {
        label: 'Severity Levels',
        type: 'multi-select',
        choices: [
          { value: 'critical', label: 'Critical' },
          { value: 'high', label: 'High' },
          { value: 'medium', label: 'Medium' },
          { value: 'low', label: 'Low' },
          { value: 'info', label: 'Info' },
        ],
      },
      concurrency: { label: 'Concurrency', type: 'number', min: 1, max: 100, step: 1 },
      rate_limit: { label: 'Rate Limit (req/s)', type: 'number', min: 10, max: 1000, step: 10 },
    },
  },
  {
    id: 'nmap',
    name: 'Nmap',
    icon: '🔍',
    description: 'Network port scanner with service detection and NSE vulnerability scripts.',
    category: 'Network Scanning',
    defaults: {
      scan_type: 'service_detection',
      ports: 'top-1000',
      timing: 3,
    },
    options: {
      scan_type: {
        label: 'Scan Type',
        type: 'select',
        choices: [
          { value: 'quick', label: 'Quick — SYN scan, minimal probing' },
          { value: 'service_detection', label: 'Service Detection — Version detection + default scripts' },
          { value: 'comprehensive', label: 'Comprehensive — Full service detection + all safe scripts' },
        ],
      },
      ports: {
        label: 'Port Range',
        type: 'select',
        choices: [
          { value: 'top-100', label: 'Top 100 ports' },
          { value: 'top-1000', label: 'Top 1000 ports' },
          { value: 'all', label: 'All 65535 ports (slow)' },
        ],
      },
      timing: {
        label: 'Timing Template',
        type: 'select',
        choices: [
          { value: 1, label: 'T1 — Sneaky (IDS evasion)' },
          { value: 2, label: 'T2 — Polite (reduced bandwidth)' },
          { value: 3, label: 'T3 — Normal (default)' },
          { value: 4, label: 'T4 — Aggressive (fast)' },
          { value: 5, label: 'T5 — Insane (fastest, may miss results)' },
        ],
      },
    },
  },
  {
    id: 'header_check',
    name: 'Security Headers',
    icon: '📋',
    description: 'Checks for missing HTTP security headers (HSTS, CSP, X-Frame-Options) and CORS misconfigurations.',
    category: 'Web Analysis',
    defaults: {
      checks: ['security_headers', 'cors'],
    },
    options: {
      checks: {
        label: 'Check Types',
        type: 'multi-select',
        choices: [
          { value: 'security_headers', label: 'Security Headers' },
          { value: 'cors', label: 'CORS Configuration' },
        ],
      },
    },
  },
  {
    id: 'ssl_analyzer',
    name: 'SSL/TLS Analyzer',
    icon: '🔒',
    description: 'Analyzes TLS configurations — weak protocols, cipher suites, certificate validity and expiry.',
    category: 'Web Analysis',
    defaults: {},
    options: {},
  },
  {
    id: 'dir_brute',
    name: 'Directory Brute-Force',
    icon: '📂',
    description: 'Enumerates hidden directories and files using wordlists. Detects admin panels, backups, API docs.',
    category: 'Web Analysis',
    defaults: {
      wordlist: 'common',
      extensions: ['.php', '.asp', '.aspx', '.jsp', '.html', '.js'],
      concurrency: 10,
    },
    options: {
      wordlist: {
        label: 'Wordlist',
        type: 'select',
        choices: [
          { value: 'common', label: 'Common (bundled — ~100 paths)' },
        ],
      },
      extensions: {
        label: 'File Extensions',
        type: 'multi-select',
        choices: [
          { value: '.php', label: '.php' },
          { value: '.asp', label: '.asp' },
          { value: '.aspx', label: '.aspx' },
          { value: '.jsp', label: '.jsp' },
          { value: '.html', label: '.html' },
          { value: '.js', label: '.js' },
          { value: '.json', label: '.json' },
          { value: '.xml', label: '.xml' },
          { value: '.txt', label: '.txt' },
          { value: '.bak', label: '.bak' },
          { value: '.old', label: '.old' },
          { value: '.conf', label: '.conf' },
        ],
      },
      concurrency: { label: 'Concurrency', type: 'number', min: 1, max: 50, step: 1 },
    },
  },
  {
    id: 'input_detect',
    name: 'Input Detection',
    icon: '📝',
    description: 'Crawls pages and detects all user input areas — forms, file uploads, search bars, login pages — mapping the attack surface.',
    category: 'Web Analysis',
    defaults: {
      max_depth: 2,
      max_pages: 50,
      concurrency: 5,
    },
    options: {
      max_depth: {
        label: 'Crawl Depth',
        type: 'select',
        choices: [
          { value: 1, label: '1 — Landing page + direct links' },
          { value: 2, label: '2 — Two levels deep (default)' },
          { value: 3, label: '3 — Three levels deep' },
          { value: 5, label: '5 — Deep crawl (slow)' },
        ],
      },
      max_pages: { label: 'Max Pages', type: 'number', min: 1, max: 500, step: 10 },
      concurrency: { label: 'Concurrency', type: 'number', min: 1, max: 20, step: 1 },
    },
  },
  {
    id: 'vuln_scanner',
    name: 'XSS/SQLi Scanner',
    icon: '🎯',
    description: 'Actively tests forms and inputs for Cross-Site Scripting (XSS) and SQL Injection vulnerabilities using payload injection.',
    category: 'Vulnerability Scanning',
    defaults: {
      concurrency: 3,
    },
    options: {
      concurrency: { label: 'Concurrency', type: 'number', min: 1, max: 10, step: 1 },
    },
  },
  {
    id: 'param_manipulator',
    name: 'Parameter Manipulation',
    icon: '🔧',
    description: 'Tests for HTTP parameter pollution, IDOR, mass assignment, type confusion, CRLF injection, and HTTP method tampering.',
    category: 'Vulnerability Scanning',
    defaults: {
      concurrency: 3,
    },
    options: {
      concurrency: { label: 'Concurrency', type: 'number', min: 1, max: 10, step: 1 },
    },
  },
  {
    id: 'access_audit',
    name: 'Access Audit',
    icon: '🔐',
    description: 'Crawls the site with primary credentials, then replays URLs with comparison credentials and unauthenticated to detect privilege escalation and forceful browsing.',
    category: 'Vulnerability Scanning',
    defaults: {
      max_depth: 3,
      max_pages: 100,
      concurrency: 5,
    },
    options: {
      max_depth: {
        label: 'Crawl Depth',
        type: 'select',
        choices: [
          { value: 1, label: '1 — Shallow' },
          { value: 2, label: '2 — Moderate' },
          { value: 3, label: '3 — Default' },
          { value: 5, label: '5 — Deep (slow)' },
        ],
      },
      max_pages: { label: 'Max Pages', type: 'number', min: 10, max: 500, step: 10 },
      concurrency: { label: 'Concurrency', type: 'number', min: 1, max: 20, step: 1 },
    },
  },
];

const CATEGORY_COLORS = {
  'Vulnerability Scanning': { bg: '#4c1d9520', border: '#8b5cf640', text: '#a78bfa' },
  'Network Scanning': { bg: '#1e3a5f20', border: '#3b82f640', text: '#60a5fa' },
  'Web Analysis': { bg: '#065f4620', border: '#10b98140', text: '#34d399' },
};

// Helper: build a clean tool_config object from form state
function buildToolConfig(toolId, config) {
  const def = AVAILABLE_TOOLS.find(t => t.id === toolId);
  if (!def) return { tool: toolId };
  const out = { tool: toolId };
  for (const [key, val] of Object.entries(config)) {
    if (val === undefined || val === null || val === '') continue;
    if (Array.isArray(val) && val.length === 0) continue;
    out[key] = val;
  }
  return out;
}

// ── MultiSelect chip picker ──────────────────────────────────────────────
function ChipPicker({ choices, selected, onChange }) {
  const toggle = (val) => {
    if (selected.includes(val)) onChange(selected.filter(v => v !== val));
    else onChange([...selected, val]);
  };
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {choices.map(c => {
        const active = selected.includes(c.value);
        return (
          <button key={c.value} type="button" onClick={() => toggle(c.value)} style={{
            padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 500, cursor: 'pointer',
            border: `1px solid ${active ? 'var(--primary)' : 'var(--border)'}`,
            background: active ? 'var(--primary)' : 'var(--bg-input)',
            color: active ? 'white' : 'var(--text)',
            transition: 'all 0.15s',
          }}>
            {c.label}
          </button>
        );
      })}
    </div>
  );
}

// ── Tool card used inside the profile modal ───────────────────────────────
function ToolConfigCard({ toolDef, config, onChange, onRemove }) {
  const catColors = CATEGORY_COLORS[toolDef.category] || { bg: '#33415520', border: '#64748b40', text: '#94a3b8' };

  const updateField = (key, val) => {
    onChange({ ...config, [key]: val });
  };

  return (
    <div style={{
      border: `1px solid ${catColors.border}`, borderRadius: 10, padding: 16, marginBottom: 12,
      background: catColors.bg, position: 'relative',
    }}>
      <button onClick={onRemove} title="Remove tool" style={{
        position: 'absolute', top: 8, right: 10, background: 'none', border: 'none',
        color: '#ef4444', cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: 4,
      }}>×</button>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <span style={{ fontSize: 20 }}>{toolDef.icon}</span>
        <div>
          <span style={{ fontWeight: 600, fontSize: 14 }}>{toolDef.name}</span>
          <span style={{
            marginLeft: 8, fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 4,
            background: catColors.bg, color: catColors.text, border: `1px solid ${catColors.border}`,
          }}>{toolDef.category}</span>
        </div>
      </div>

      {Object.entries(toolDef.options).length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {Object.entries(toolDef.options).map(([key, opt]) => {
            const val = config[key] ?? toolDef.defaults[key];
            if (opt.type === 'multi-select') {
              return (
                <div key={key}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 4, color: 'var(--text-secondary)' }}>
                    {opt.label}
                  </label>
                  <ChipPicker choices={opt.choices} selected={val || []} onChange={v => updateField(key, v)} />
                </div>
              );
            }
            if (opt.type === 'select') {
              return (
                <div key={key}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 4, color: 'var(--text-secondary)' }}>
                    {opt.label}
                  </label>
                  <select value={val ?? ''} onChange={e => {
                    const v = e.target.value;
                    updateField(key, isNaN(v) ? v : Number(v));
                  }} style={{
                    width: '100%', padding: '6px 10px', borderRadius: 6, fontSize: 13,
                    border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text)',
                  }}>
                    {opt.choices.map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
              );
            }
            if (opt.type === 'number') {
              return (
                <div key={key}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 4, color: 'var(--text-secondary)' }}>
                    {opt.label}
                  </label>
                  <input type="number" min={opt.min} max={opt.max} step={opt.step} value={val ?? ''} onChange={e => updateField(key, Number(e.target.value))}
                    style={{
                      width: 120, padding: '6px 10px', borderRadius: 6, fontSize: 13,
                      border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text)',
                    }} />
                </div>
              );
            }
            return null;
          })}
        </div>
      )}
      {Object.entries(toolDef.options).length === 0 && (
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontStyle: 'italic' }}>No additional configuration needed</div>
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────
export default function AutoScan() {
  const { apiFetch } = useAuth();
  const navigate = useNavigate();
  const [scans, setScans] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [engagements, setEngagements] = useState([]);
  const [health, setHealth] = useState(null);
  const [activeTab, setActiveTab] = useState('scans');
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({ engagement_id: '', profile_id: '', cron_expression: '' });

  // Profile modal state
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [profileForm, setProfileForm] = useState({ name: '', description: '', engagement_type: '', concurrency_limit: 3, is_default: false });
  const [selectedTools, setSelectedTools] = useState([]); // [{toolId, config}]
  const [profileError, setProfileError] = useState('');
  const [profileSaving, setProfileSaving] = useState(false);

  // Payload management state
  const [showPayloadModal, setShowPayloadModal] = useState(false);
  const [payloadForm, setPayloadForm] = useState({ xss: '', sqli_error: '', sqli_boolean: '' });
  const [payloadSaving, setPayloadSaving] = useState(false);
  const [payloadError, setPayloadError] = useState('');

  const orchFetch = useCallback(async (path, options = {}) => {
    const token = sessionStorage.getItem('token');
    const headers = {
      ...(options.isFormData ? {} : { 'Content-Type': 'application/json' }),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    };
    return await fetch(`${SCAN_API}${path}`, { ...options, headers });
  }, []);

  const loadAll = useCallback(async () => {
    try {
      const [scansRes, profilesRes, schedulesRes, engRes, healthRes] = await Promise.all([
        orchFetch('/scans?limit=50'),
        orchFetch('/profiles'),
        orchFetch('/schedules'),
        apiFetch('/engagements'),
        fetch('/orchestrator-api/health').catch(() => null),
      ]);

      if (scansRes.ok) {
        const data = await scansRes.json();
        setScans(data.items || []);
      }
      if (profilesRes.ok) setProfiles(await profilesRes.json());
      if (schedulesRes.ok) setSchedules(await schedulesRes.json());
      if (engRes.ok) setEngagements(await engRes.json());
      if (healthRes?.ok) setHealth(await healthRes.json());
    } catch (err) {
      console.error('Failed to load scan data:', err);
    }
  }, [orchFetch, apiFetch]);

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    const hasRunning = scans.some(s => s.status === 'queued' || s.status === 'running');
    if (!hasRunning) return;
    const interval = setInterval(loadAll, 8000);
    return () => clearInterval(interval);
  }, [scans, loadAll]);

  // ── Profile CRUD ──────────────────────────────────────────────────────
  const openNewProfile = () => {
    setEditingProfile(null);
    setProfileForm({ name: '', description: '', engagement_type: '', concurrency_limit: 3, is_default: false });
    setSelectedTools([]);
    setProfileError('');
    setShowProfileModal(true);
  };

  const openEditProfile = (profile) => {
    setEditingProfile(profile);
    setProfileForm({
      name: profile.name,
      description: profile.description || '',
      engagement_type: profile.engagement_type,
      concurrency_limit: profile.concurrency_limit,
      is_default: profile.is_default,
    });
    // Hydrate tool configs
    setSelectedTools((profile.tool_configs || []).map(tc => {
      const { tool, ...config } = tc;
      return { toolId: tool, config };
    }));
    setProfileError('');
    setShowProfileModal(true);
  };

  const addTool = (toolId) => {
    if (selectedTools.some(t => t.toolId === toolId)) return;
    const def = AVAILABLE_TOOLS.find(t => t.id === toolId);
    setSelectedTools(prev => [...prev, { toolId, config: { ...def.defaults } }]);
  };

  const removeTool = (toolId) => {
    setSelectedTools(prev => prev.filter(t => t.toolId !== toolId));
  };

  const updateToolConfig = (toolId, newConfig) => {
    setSelectedTools(prev => prev.map(t => t.toolId === toolId ? { ...t, config: newConfig } : t));
  };

  const saveProfile = async () => {
    setProfileError('');
    if (!profileForm.name.trim()) { setProfileError('Profile name is required'); return; }
    if (!profileForm.engagement_type) { setProfileError('Engagement type is required'); return; }
    if (selectedTools.length === 0) { setProfileError('Select at least one scan tool'); return; }

    const tool_configs = selectedTools.map(t => buildToolConfig(t.toolId, t.config));
    const payload = {
      name: profileForm.name.trim(),
      description: profileForm.description.trim() || null,
      engagement_type: profileForm.engagement_type,
      tool_configs,
      concurrency_limit: profileForm.concurrency_limit,
      is_default: profileForm.is_default,
    };

    setProfileSaving(true);
    try {
      const url = editingProfile ? `/profiles/${editingProfile.id}` : '/profiles';
      const method = editingProfile ? 'PUT' : 'POST';
      const res = await orchFetch(url, { method, body: JSON.stringify(payload) });
      if (res.ok) {
        setShowProfileModal(false);
        loadAll();
      } else {
        const err = await res.json().catch(() => ({}));
        setProfileError(err.detail || 'Failed to save profile');
      }
    } catch {
      setProfileError('Network error saving profile');
    } finally {
      setProfileSaving(false);
    }
  };

  const deleteProfile = async (id) => {
    if (!window.confirm('Delete this scan profile? This cannot be undone.')) return;
    await orchFetch(`/profiles/${id}`, { method: 'DELETE' });
    loadAll();
  };

  // ── Schedule CRUD ─────────────────────────────────────────────────────
  const deleteSchedule = async (id) => {
    if (!window.confirm('Delete this schedule?')) return;
    await orchFetch(`/schedules/${id}`, { method: 'DELETE' });
    loadAll();
  };

  const toggleSchedule = async (schedule) => {
    await orchFetch(`/schedules/${schedule.id}`, {
      method: 'PUT',
      body: JSON.stringify({ is_active: !schedule.is_active }),
    });
    loadAll();
  };

  const createSchedule = async () => {
    if (!scheduleForm.engagement_id || !scheduleForm.profile_id || !scheduleForm.cron_expression) return;
    const res = await orchFetch('/schedules', {
      method: 'POST',
      body: JSON.stringify(scheduleForm),
    });
    if (res.ok) {
      setShowScheduleModal(false);
      setScheduleForm({ engagement_id: '', profile_id: '', cron_expression: '' });
      loadAll();
    } else {
      const err = await res.json();
      alert(err.detail || 'Failed to create schedule');
    }
  };

  // ── Payload management ──────────────────────────────────────────────
  const openPayloadModal = async () => {
    setPayloadError('');
    try {
      const res = await apiFetch('/scan-settings/payloads');
      if (res.ok) {
        const data = await res.json();
        setPayloadForm({
          xss: (data.xss || []).join('\n'),
          sqli_error: (data.sqli_error || []).join('\n'),
          sqli_boolean: (data.sqli_boolean || []).map(pair => pair.join(' | ')).join('\n'),
        });
      }
    } catch {
      // Use empty defaults
    }
    setShowPayloadModal(true);
  };

  const savePayloads = async () => {
    setPayloadError('');
    const xss = payloadForm.xss.split('\n').map(l => l.trim()).filter(Boolean);
    const sqli_error = payloadForm.sqli_error.split('\n').map(l => l.trim()).filter(Boolean);
    const sqli_boolean = payloadForm.sqli_boolean.split('\n').map(l => l.trim()).filter(Boolean)
      .map(line => {
        const parts = line.split('|').map(p => p.trim());
        return parts.length >= 2 ? [parts[0], parts[1]] : [parts[0], "' AND '1'='2"];
      });

    if (xss.length === 0 && sqli_error.length === 0 && sqli_boolean.length === 0) {
      setPayloadError('At least one payload category must have entries');
      return;
    }

    setPayloadSaving(true);
    try {
      const res = await apiFetch('/scan-settings/payloads', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ xss, sqli_error, sqli_boolean }),
      });
      if (res.ok) {
        setShowPayloadModal(false);
      } else {
        const err = await res.json().catch(() => ({}));
        setPayloadError(err.error || 'Failed to save payloads');
      }
    } catch {
      setPayloadError('Network error saving payloads');
    } finally {
      setPayloadSaving(false);
    }
  };

  const resetPayloads = async () => {
    if (!window.confirm('Reset all payloads to defaults?')) return;
    setPayloadSaving(true);
    try {
      const res = await apiFetch('/scan-settings/payloads/reset', { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        setPayloadForm({
          xss: (data.xss || []).join('\n'),
          sqli_error: (data.sqli_error || []).join('\n'),
          sqli_boolean: (data.sqli_boolean || []).map(pair => pair.join(' | ')).join('\n'),
        });
      }
    } catch {}
    setPayloadSaving(false);
  };

  // ── Stats & helpers ───────────────────────────────────────────────────
  const stats = {
    total: scans.length,
    running: scans.filter(s => s.status === 'running' || s.status === 'queued').length,
    completed: scans.filter(s => s.status === 'completed').length,
    findings: scans.reduce((sum, s) => sum + (s.total_findings || 0), 0),
  };

  const formatTime = (ts) => {
    if (!ts) return '-';
    return new Date(ts).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const tabs = [
    { id: 'scans', label: 'Scan History', icon: '📊' },
    { id: 'profiles', label: 'Scan Profiles', icon: '📋' },
    { id: 'schedules', label: 'Schedules', icon: '🕐' },
    { id: 'settings', label: 'Settings', icon: '⚙️' },
  ];

  const availableToAdd = AVAILABLE_TOOLS.filter(t => !selectedTools.some(s => s.toolId === t.id));

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 4 }}>Automated Scanning</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
            Manage automated penetration test scans, profiles, and schedules
          </p>
        </div>
        {health && (
          <div style={{
            display: 'flex', gap: 8, alignItems: 'center', padding: '8px 14px',
            borderRadius: 8, background: health.status === 'ok' ? '#14532d20' : '#991b1b20',
            border: `1px solid ${health.status === 'ok' ? '#22c55e40' : '#ef444440'}`,
          }}>
            <span style={{
              width: 8, height: 8, borderRadius: '50%',
              background: health.status === 'ok' ? '#22c55e' : '#ef4444',
            }} />
            <span style={{ fontSize: 12, fontWeight: 500, color: health.status === 'ok' ? '#22c55e' : '#ef4444' }}>
              Orchestrator {health.status === 'ok' ? 'Online' : 'Degraded'}
            </span>
          </div>
        )}
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
        <StatCard label="Total Scans" value={stats.total} icon="🔬" color="#3b82f6" />
        <StatCard label="Active" value={stats.running} icon="⚡" color="#22c55e" />
        <StatCard label="Completed" value={stats.completed} icon="✅" color="#8b5cf6" />
        <StatCard label="Findings Found" value={stats.findings} icon="🐛" color="#ef4444" />
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid var(--border)', paddingBottom: 0 }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '10px 20px', border: 'none', cursor: 'pointer',
              fontSize: 14, fontWeight: 500, background: 'transparent',
              color: activeTab === tab.id ? 'var(--primary)' : 'var(--text-secondary)',
              borderBottom: activeTab === tab.id ? '2px solid var(--primary)' : '2px solid transparent',
              transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            <span>{tab.icon}</span> {tab.label}
          </button>
        ))}
      </div>

      {/* Scan History Tab */}
      {activeTab === 'scans' && (
        <Card>
          <Table
            columns={[
              {
                key: 'status', label: 'Status', render: (val) => {
                  const c = STATUS_COLORS[val] || { bg: '#334155', color: '#e2e8f0' };
                  return <span style={{
                    padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                    background: c.bg, color: c.color, textTransform: 'capitalize',
                  }}>{val === 'running' && '● '}{val}</span>;
                },
              },
              {
                key: 'engagement_id', label: 'Engagement', render: (val) => {
                  const eng = engagements.find(e => e.id === val);
                  return (
                    <span style={{ cursor: 'pointer', color: 'var(--primary)', fontSize: 13 }}
                      onClick={() => navigate(`/engagements/${val}`)}>
                      {eng?.name || val?.substring(0, 8) + '...'}
                    </span>
                  );
                },
              },
              {
                key: 'tasks', label: 'Tools', render: (val) => (
                  <div style={{ display: 'flex', gap: 4 }}>
                    {(val || []).map(t => (
                      <span key={t.id} title={t.tool_name} style={{ fontSize: 14 }}>
                        {TOOL_ICONS[t.tool_name] || '🔧'}
                      </span>
                    ))}
                  </div>
                ),
              },
              {
                key: 'progress_percent', label: 'Progress', render: (val) => (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 80, height: 6, borderRadius: 3, background: 'var(--border)' }}>
                      <div style={{
                        width: `${val}%`, height: '100%', borderRadius: 3,
                        background: val === 100 ? '#22c55e' : '#3b82f6',
                        transition: 'width 0.5s',
                      }} />
                    </div>
                    <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{val}%</span>
                  </div>
                ),
              },
              { key: 'total_findings', label: 'Findings', render: (val) => val || 0 },
              { key: 'created_at', label: 'Started', render: (val) => formatTime(val) },
              {
                key: 'id', label: '', render: (val, row) => (
                  <Button variant="ghost" style={{ padding: '4px 8px', fontSize: 12 }}
                    onClick={() => navigate(`/engagements/${row.engagement_id}`)}>
                    View →
                  </Button>
                ),
              },
            ]}
            data={scans}
          />
        </Card>
      )}

      {/* Profiles Tab */}
      {activeTab === 'profiles' && (
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
            <div style={{ fontSize: 14, color: 'var(--text-secondary)' }}>
              {profiles.length} scan profile{profiles.length !== 1 ? 's' : ''} configured
            </div>
            <Button onClick={openNewProfile}>+ New Profile</Button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 }}>
            {profiles.map(profile => (
              <div key={profile.id} style={{
                border: '1px solid var(--border)', borderRadius: 10, padding: 20,
                background: 'var(--bg-input)', transition: 'border-color 0.2s',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 15 }}>{profile.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                      {profile.engagement_type}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    {profile.is_default && (
                      <Badge severity="Active">Default</Badge>
                    )}
                  </div>
                </div>
                {profile.description && (
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>
                    {profile.description}
                  </div>
                )}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {profile.tool_configs?.map((tc, i) => (
                    <span key={i} style={{
                      padding: '3px 8px', borderRadius: 6, fontSize: 11, fontWeight: 500,
                      background: 'var(--bg-card)', border: '1px solid var(--border)',
                    }}>
                      {TOOL_ICONS[tc.tool] || '🔧'} {tc.tool}
                    </span>
                  ))}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                    Concurrency: {profile.concurrency_limit}
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => openEditProfile(profile)} style={{
                      padding: '4px 10px', borderRadius: 5, fontSize: 12, cursor: 'pointer',
                      border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text)',
                    }}>Edit</button>
                    <button onClick={() => deleteProfile(profile.id)} style={{
                      padding: '4px 10px', borderRadius: 5, fontSize: 12, cursor: 'pointer',
                      border: '1px solid #ef444440', background: 'transparent', color: '#ef4444',
                    }}>Delete</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Schedules Tab */}
      {activeTab === 'schedules' && (
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
            <div style={{ fontSize: 14, color: 'var(--text-secondary)' }}>
              {schedules.filter(s => s.is_active).length} active schedules
            </div>
            <Button onClick={() => setShowScheduleModal(true)}>+ New Schedule</Button>
          </div>
          <Table
            columns={[
              {
                key: 'is_active', label: 'Status', render: (val) => (
                  <Badge severity={val ? 'In Progress' : 'Cancelled'}>{val ? 'Active' : 'Paused'}</Badge>
                ),
              },
              {
                key: 'engagement_id', label: 'Engagement', render: (val) => {
                  const eng = engagements.find(e => e.id === val);
                  return eng?.name || val?.substring(0, 8) + '...';
                },
              },
              {
                key: 'profile_id', label: 'Profile', render: (val) => {
                  const p = profiles.find(pr => pr.id === val);
                  return p?.name || '-';
                },
              },
              { key: 'cron_expression', label: 'Schedule', render: (val) => (
                <code style={{ fontSize: 12, padding: '2px 6px', borderRadius: 4, background: 'var(--bg-input)' }}>{val}</code>
              )},
              { key: 'next_run_at', label: 'Next Run', render: (val) => formatTime(val) },
              { key: 'last_run_at', label: 'Last Run', render: (val) => formatTime(val) },
              {
                key: 'id', label: '', render: (val, row) => (
                  <div style={{ display: 'flex', gap: 4 }}>
                    <Button variant="ghost" style={{ padding: '4px 8px', fontSize: 12 }}
                      onClick={() => toggleSchedule(row)}>
                      {row.is_active ? '⏸ Pause' : '▶ Resume'}
                    </Button>
                    <Button variant="ghost" style={{ padding: '4px 8px', fontSize: 12, color: '#ef4444' }}
                      onClick={() => deleteSchedule(val)}>
                      🗑
                    </Button>
                  </div>
                ),
              },
            ]}
            data={schedules}
          />
        </Card>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <Card>
          <div style={{ maxWidth: 600 }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>XSS / SQLi Payloads</h3>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
              Customize the payloads used by the XSS/SQLi vulnerability scanner. Changes apply to all future scans.
            </p>
            <Button onClick={openPayloadModal}>🎯 Manage Payloads</Button>
          </div>
        </Card>
      )}

      {/* Payload Management Modal */}
      <Modal isOpen={showPayloadModal} onClose={() => setShowPayloadModal(false)} title="Manage XSS / SQLi Payloads" width={650}>
        {payloadError && (
          <div style={{ padding: '10px 14px', borderRadius: 8, background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', marginBottom: 16, fontSize: 13 }}>
            {payloadError}
          </div>
        )}

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: 'var(--text)' }}>
            XSS Payloads <span style={{ fontWeight: 400, color: 'var(--text-secondary)' }}>(one per line)</span>
          </label>
          <textarea
            value={payloadForm.xss}
            onChange={e => setPayloadForm(f => ({ ...f, xss: e.target.value }))}
            rows={6}
            placeholder={"<script>alert(1)</script>\n\"><img src=x onerror=alert(1)>"}
            style={{
              width: '100%', padding: '10px 12px', borderRadius: 8, fontSize: 12,
              border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text)',
              fontFamily: 'monospace', resize: 'vertical',
            }}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: 'var(--text)' }}>
            SQLi Error-Based Payloads <span style={{ fontWeight: 400, color: 'var(--text-secondary)' }}>(one per line)</span>
          </label>
          <textarea
            value={payloadForm.sqli_error}
            onChange={e => setPayloadForm(f => ({ ...f, sqli_error: e.target.value }))}
            rows={5}
            placeholder={"'\n' OR '1'='1\n1' AND '1'='2' --"}
            style={{
              width: '100%', padding: '10px 12px', borderRadius: 8, fontSize: 12,
              border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text)',
              fontFamily: 'monospace', resize: 'vertical',
            }}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: 'var(--text)' }}>
            SQLi Boolean Payload Pairs <span style={{ fontWeight: 400, color: 'var(--text-secondary)' }}>(true_payload | false_payload, one pair per line)</span>
          </label>
          <textarea
            value={payloadForm.sqli_boolean}
            onChange={e => setPayloadForm(f => ({ ...f, sqli_boolean: e.target.value }))}
            rows={3}
            placeholder={"' OR '1'='1 | ' AND '1'='2\n1 OR 1=1 | 1 AND 1=2"}
            style={{
              width: '100%', padding: '10px 12px', borderRadius: 8, fontSize: 12,
              border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text)',
              fontFamily: 'monospace', resize: 'vertical',
            }}
          />
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>
            Each line should contain a "true" payload and "false" payload separated by a pipe (|). The scanner compares response differences between them.
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'space-between', marginTop: 8 }}>
          <Button variant="secondary" onClick={resetPayloads} disabled={payloadSaving}
            style={{ color: '#f59e0b', borderColor: '#f59e0b40' }}>
            Reset to Defaults
          </Button>
          <div style={{ display: 'flex', gap: 12 }}>
            <Button variant="secondary" onClick={() => setShowPayloadModal(false)}>Cancel</Button>
            <Button onClick={savePayloads} disabled={payloadSaving}>
              {payloadSaving ? 'Saving...' : 'Save Payloads'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* ── Create / Edit Profile Modal ─────────────────────────────────── */}
      <Modal isOpen={showProfileModal} onClose={() => setShowProfileModal(false)}
        title={editingProfile ? 'Edit Scan Profile' : 'Create Scan Profile'} width={700}>
        {profileError && (
          <div style={{ padding: '10px 14px', borderRadius: 8, background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', marginBottom: 16, fontSize: 13 }}>
            {profileError}
          </div>
        )}

        {/* Basic info */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 4 }}>
          <Input label="Profile Name" placeholder="e.g., Web App - Full Scan" value={profileForm.name}
            onChange={e => setProfileForm(f => ({ ...f, name: e.target.value }))} />
          <Select label="Engagement Type"
            options={[
              { value: '', label: 'Select type...' },
              ...ENG_TYPES.map(t => ({ value: t, label: t })),
            ]}
            value={profileForm.engagement_type}
            onChange={e => setProfileForm(f => ({ ...f, engagement_type: e.target.value }))} />
        </div>
        <Input label="Description (optional)" placeholder="Brief description of what this profile scans" value={profileForm.description}
          onChange={e => setProfileForm(f => ({ ...f, description: e.target.value }))} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: 'var(--text-secondary)' }}>
              Concurrency Limit
            </label>
            <input type="number" min={1} max={10} value={profileForm.concurrency_limit}
              onChange={e => setProfileForm(f => ({ ...f, concurrency_limit: Number(e.target.value) }))}
              style={{
                width: 80, padding: '8px 10px', borderRadius: 8, fontSize: 14,
                border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text)',
              }} />
            <span style={{ fontSize: 11, color: 'var(--text-secondary)', marginLeft: 8 }}>Max tools running in parallel</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 8 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: 'var(--text-secondary)' }}>
              <input type="checkbox" checked={profileForm.is_default}
                onChange={e => setProfileForm(f => ({ ...f, is_default: e.target.checked }))}
                style={{ width: 16, height: 16, accentColor: 'var(--primary)' }} />
              Set as default for this engagement type
            </label>
          </div>
        </div>

        {/* Divider */}
        <div style={{ borderTop: '1px solid var(--border)', marginBottom: 16, paddingTop: 16 }}>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12 }}>Scan Tools</div>
        </div>

        {/* Selected tools */}
        {selectedTools.map(st => {
          const def = AVAILABLE_TOOLS.find(t => t.id === st.toolId);
          if (!def) return null;
          return (
            <ToolConfigCard key={st.toolId} toolDef={def} config={st.config}
              onChange={(c) => updateToolConfig(st.toolId, c)}
              onRemove={() => removeTool(st.toolId)} />
          );
        })}

        {/* Add tool picker */}
        {availableToAdd.length > 0 && (
          <div style={{
            border: '2px dashed var(--border)', borderRadius: 10, padding: 16, marginBottom: 16,
          }}>
            <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 10, color: 'var(--text-secondary)' }}>
              Add a scan tool
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8 }}>
              {availableToAdd.map(tool => {
                const catColors = CATEGORY_COLORS[tool.category] || { bg: '#33415520', border: '#64748b40', text: '#94a3b8' };
                return (
                  <button key={tool.id} onClick={() => addTool(tool.id)} style={{
                    display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px',
                    borderRadius: 8, border: `1px solid ${catColors.border}`, background: catColors.bg,
                    cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s',
                  }}>
                    <span style={{ fontSize: 22, lineHeight: 1 }}>{tool.icon}</span>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)', marginBottom: 2 }}>{tool.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-secondary)', lineHeight: 1.3 }}>{tool.description}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {selectedTools.length === 0 && (
          <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--text-secondary)', fontSize: 13 }}>
            Click a tool above to add it to this profile
          </div>
        )}

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 8 }}>
          <Button variant="secondary" onClick={() => setShowProfileModal(false)}>Cancel</Button>
          <Button onClick={saveProfile} disabled={profileSaving}>
            {profileSaving ? 'Saving...' : editingProfile ? 'Save Changes' : 'Create Profile'}
          </Button>
        </div>
      </Modal>

      {/* New Schedule Modal */}
      <Modal isOpen={showScheduleModal} onClose={() => setShowScheduleModal(false)} title="Create Scan Schedule" width={500}>
        <Select
          label="Engagement"
          options={[
            { value: '', label: 'Select engagement...' },
            ...engagements.filter(e => e.status !== 'Complete' && e.status !== 'Cancelled').map(e => ({
              value: e.id, label: e.name,
            })),
          ]}
          value={scheduleForm.engagement_id}
          onChange={e => setScheduleForm(f => ({ ...f, engagement_id: e.target.value }))}
        />
        <Select
          label="Scan Profile"
          options={[
            { value: '', label: 'Select profile...' },
            ...profiles.map(p => ({ value: p.id, label: `${p.name} (${p.engagement_type})` })),
          ]}
          value={scheduleForm.profile_id}
          onChange={e => setScheduleForm(f => ({ ...f, profile_id: e.target.value }))}
        />
        <Input
          label="Cron Expression"
          placeholder="0 9 * * 1-5 (weekdays at 9am)"
          value={scheduleForm.cron_expression}
          onChange={e => setScheduleForm(f => ({ ...f, cron_expression: e.target.value }))}
        />
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: -8, marginBottom: 16 }}>
          Format: minute hour day-of-month month day-of-week. Examples:<br />
          <code>0 9 * * 1-5</code> = Weekdays at 9:00 AM &nbsp;|&nbsp;
          <code>0 0 * * 0</code> = Sundays at midnight &nbsp;|&nbsp;
          <code>0 */6 * * *</code> = Every 6 hours
        </div>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={() => setShowScheduleModal(false)}>Cancel</Button>
          <Button onClick={createSchedule}>Create Schedule</Button>
        </div>
      </Modal>
    </div>
  );
}
